import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Stethoscope, UserPlus, RefreshCcw, Building2, 
  Loader2, Save, Trash2, X, Check, Eye, EyeOff 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AVAILABLE_ROOMS = ['Stanza A', 'Stanza B', 'Stanza C', 'Stanza D'];

const DoctorsManager = () => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    specialization: '',
    staff_type: 'doctor',
    room: 'Stanza A'
  });

  useEffect(() => {
    loadDoctors();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadDoctors = async () => {
    setLoading(true);
    try {
      // Use public /doctors endpoint - it returns all doctors/ortottisti
      const res = await axios.get(`${API}/doctors`, getAuthHeaders());
      setDoctors(res.data.doctors || []);
    } catch (error) {
      console.error('Error loading doctors:', error);
      toast.error('Errore caricamento staff');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    setSaving('new');
    try {
      await axios.post(`${API}/admin/doctors`, formData, getAuthHeaders());
      toast.success('Staff aggiunto con successo!');
      setShowAddModal(false);
      resetForm();
      loadDoctors();
    } catch (error) {
      console.error('Error adding doctor:', error);
      toast.error(error.response?.data?.detail || 'Errore aggiunta staff');
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateRoom = async (doctorId, newRoom) => {
    setSaving(doctorId);
    try {
      await axios.put(`${API}/admin/doctors/${doctorId}`, { room: newRoom }, getAuthHeaders());
      toast.success('Stanza aggiornata');
      loadDoctors();
    } catch (error) {
      console.error('Error updating room:', error);
      toast.error('Errore aggiornamento stanza');
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteDoctor = async (doctorId) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questo membro dello staff?')) return;
    
    setDeleting(doctorId);
    try {
      await axios.delete(`${API}/admin/doctors/${doctorId}`, getAuthHeaders());
      toast.success('Staff rimosso');
      loadDoctors();
    } catch (error) {
      console.error('Error deleting doctor:', error);
      toast.error('Errore rimozione staff');
    } finally {
      setDeleting(null);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      specialization: '',
      staff_type: 'doctor',
      room: 'Stanza A'
    });
  };

  const roomColors = {
    'Stanza A': { bg: '#DBEAFE', border: '#2A9FCB', text: '#2A9FCB' },
    'Stanza B': { bg: '#FEF3C7', border: '#F59E0B', text: '#B45309' },
    'Stanza C': { bg: '#D1FAE5', border: '#10B981', text: '#047857' },
    'Stanza D': { bg: '#EDE9FE', border: '#8B5CF6', text: '#6D28D9' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2 text-[#2A9FCB]">
            <Stethoscope className="w-6 h-6" />
            Staff Medico (Oculisti e Ortottisti)
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-[#2A9FCB] hover:bg-[#2387b3] transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Aggiungi Staff
            </button>
            <button
              onClick={loadDoctors}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#2A9FCB] text-[#2A9FCB] hover:bg-[#2A9FCB]/5"
              disabled={loading}
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
          </div>
        </div>

        {/* Rooms Overview */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {AVAILABLE_ROOMS.map(room => {
            const roomDoctors = doctors.filter(d => d.room === room);
            const c = roomColors[room];
            return (
              <div 
                key={room} 
                className="p-4 rounded-xl border-2"
                style={{ background: c.bg, borderColor: c.border }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-5 h-5" style={{ color: c.text }} />
                  <h3 className="font-bold" style={{ color: c.text }}>{room}</h3>
                </div>
                <p className="text-2xl font-bold" style={{ color: c.text }}>
                  {roomDoctors.length}
                </p>
                <p className="text-sm" style={{ color: c.text }}>
                  {roomDoctors.length === 1 ? 'medico' : 'medici'}
                </p>
                <div className="mt-2 space-y-1">
                  {roomDoctors.map(d => (
                    <p key={d.id} className="text-xs truncate" style={{ color: c.text }}>
                      {d.role === 'ortottista' ? 'Ort.' : 'Dott.'} {d.last_name}
                    </p>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Doctors Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3 font-medium text-[#2A9FCB]/80">Nome</th>
                <th className="text-left p-3 font-medium text-[#2A9FCB]/80">Tipo</th>
                <th className="text-left p-3 font-medium text-[#2A9FCB]/80">Email</th>
                <th className="text-left p-3 font-medium text-[#2A9FCB]/80">Specializzazione</th>
                <th className="text-left p-3 font-medium text-[#2A9FCB]/80">Stanza</th>
                <th className="text-left p-3 font-medium text-[#2A9FCB]/80">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {doctors.map(doctor => (
                <tr key={doctor.id} className="border-b hover:bg-gray-50">
                  <td className="p-3">
                    <div className="font-medium">
                      {doctor.role === 'ortottista' ? 'Ort.' : 'Dott.'} {doctor.first_name} {doctor.last_name}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      doctor.role === 'ortottista' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-emerald-100 text-emerald-700'
                    }`}>
                      {doctor.role === 'ortottista' ? '👁️ Ortottista' : '🩺 Oculista'}
                    </span>
                  </td>
                  <td className="p-3 text-sm text-[#2A9FCB]/70">{doctor.email}</td>
                  <td className="p-3 text-sm">{doctor.specialization || '-'}</td>
                  <td className="p-3">
                    <select
                      value={doctor.room || 'Stanza A'}
                      onChange={(e) => handleUpdateRoom(doctor.id, e.target.value)}
                      className="px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-[#2A9FCB]"
                      disabled={saving === doctor.id}
                    >
                      {AVAILABLE_ROOMS.map(room => (
                        <option key={room} value={room}>{room}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => handleDeleteDoctor(doctor.id)}
                      disabled={deleting === doctor.id}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      {deleting === doctor.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {doctors.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Stethoscope className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Nessun membro dello staff medico</p>
          </div>
        )}
      </div>

      {/* Add Doctor Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#2A9FCB]">Aggiungi Staff Medico</h3>
              <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleAddDoctor} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] pr-10"
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
                  <select
                    value={formData.staff_type}
                    onChange={(e) => setFormData({...formData, staff_type: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  >
                    <option value="doctor">🩺 Oculista</option>
                    <option value="ortottista">👁️ Ortottista</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Stanza</label>
                  <select
                    value={formData.room}
                    onChange={(e) => setFormData({...formData, room: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  >
                    {AVAILABLE_ROOMS.map(room => (
                      <option key={room} value={room}>{room}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Specializzazione</label>
                <input
                  type="text"
                  value={formData.specialization}
                  onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                  placeholder="Es: Strabismo, Cataratta, Glaucoma..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving === 'new'}
                  className="flex-1 px-4 py-2 bg-[#2A9FCB] text-white rounded-lg hover:bg-[#2387b3] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving === 'new' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Aggiungi
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorsManager;
